import React, { useState, useEffect } from 'react';
import { getUnansweredQuestions } from '../config/onboardingQuestions';
import IntroScreen from './onboarding/IntroScreen';
import QuestionScreen from './onboarding/QuestionScreen';
import NotificationEducationScreen from './onboarding/NotificationEducationScreen';
import PWAEducationScreen from './onboarding/PWAEducationScreen';
import PlanApprovalScreen from './onboarding/PlanApprovalScreen';
import TodosLoadingScreen from './onboarding/TodosLoadingScreen';
import LoadingScreen from './LoadingScreen';
import * as mixpanelService from '../services/mixpanelService';
import styles from '../styles/OnboardingScreen.module.css';

const LOADING_PHRASES = [
  { text: 'Building your financial confidence...', image: 'Building Your Financial Confidence.png' },
  { text: 'Empowering your investment journey...', image: 'Empowering Your Investment Journey.png' },
  { text: 'Your wealth, your way...', image: 'Your Wealth, Your Way.png' },
  { text: 'Creating your personalised path...', image: 'Creating Your Personalized Path.png' },
  { text: 'Investing in your future...', image: 'Investing in Your Future.png' },
  { text: 'Growing your financial independence...', image: 'Generated Image October 22, 2025 - 11_55PM.png' }
];

const MISSION_LOADING_PHRASE = { text: 'Creating your personalised action plan...', image: 'Creating Your Personalized Path.png' };

function OnboardingScreen({ user, onComplete, onUpdateUser, onDismiss, flowItems: externalFlowItems, flowType }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flowItems, setFlowItems] = useState(null); // null = loading, [] = no items, [...] = has items
  const [isInitialized, setIsInitialized] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [loadingPhrase, setLoadingPhrase] = useState(LOADING_PHRASES[0].text);
  const [approvalPromise, setApprovalPromise] = useState(null); // Tracks async plan approval
  const isChangeObjectiveFlow = flowType === 'change-objective'; // Track if this is a change-objective flow

  useEffect(() => {
    // Only initialize once - don't recalculate when user updates mid-flow
    if (isInitialized) {
      return;
    }

    console.log('[OnboardingScreen] Initializing...');

    // Use external flow items if provided (for change-objective), otherwise get unanswered questions
    const allItems = externalFlowItems || getUnansweredQuestions(user);
    console.log('[OnboardingScreen] Flow items:', allItems.length);

    setFlowItems(allItems);
    setIsInitialized(true);
  }, [user, isInitialized, externalFlowItems]);

  // Track onboarding step viewed
  useEffect(() => {
    if (isInitialized && flowItems && flowItems.length > 0 && user) {
      const currentItem = flowItems[currentIndex];
      mixpanelService.trackEvent('Onboarding Step Viewed', {
        step_type: currentItem.type,
        step_field: currentItem.field || currentItem.type,
        step_number: currentIndex + 1,
        total_steps: flowItems.length,
        is_change_objective: isChangeObjectiveFlow,
      });
    }
  }, [isInitialized, flowItems, currentIndex, isChangeObjectiveFlow, user]);

  // If no questions to show, complete onboarding (only after initialization)
  useEffect(() => {
    // console.log('[OnboardingScreen] useEffect check - isInitialized:', isInitialized, 'flowItems:', flowItems, 'length:', flowItems?.length);
    if (isInitialized && flowItems && flowItems.length === 0) {
      // console.log('[OnboardingScreen] No items after initialization, completing onboarding');
      onComplete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowItems, isInitialized]); // Don't include onComplete to avoid re-triggering

  const handleNext = async (answer) => {
    const currentItem = flowItems[currentIndex];
    const isLastItem = currentIndex >= flowItems.length - 1;

    // Track Onboarding Step Completed
    if (user) {
      mixpanelService.trackEvent('Onboarding Step Completed', {
        step_type: currentItem.type,
        step_field: currentItem.field || currentItem.type,
        step_number: currentIndex + 1,
        total_steps: flowItems.length,
        is_change_objective: isChangeObjectiveFlow,
        answer_provided: answer !== undefined && answer !== null,
      });
    }

    // If answer is a Promise (from plan approval), store it for TodosLoadingScreen
    if (answer instanceof Promise) {
      console.log('[OnboardingScreen] Received approval promise, storing for todos-loading screen');
      setApprovalPromise(answer);
      // Continue to next screen immediately
      if (!isLastItem) {
        setCurrentIndex(currentIndex + 1);
        setIsTransitioning(false);
      } else {
        console.log('[OnboardingScreen] Last item reached - completing onboarding');
        onComplete();
      }
      return;
    }

    // If it's a question, save the answer
    if (currentItem.type === 'question') {
      const newAnswers = {
        ...answers,
        [currentItem.field]: answer
      };
      setAnswers(newAnswers);

      // Show loading with special phrase for mission, random for others
      const loadingData = currentItem.field === 'currentMission'
        ? MISSION_LOADING_PHRASE
        : LOADING_PHRASES[Math.floor(Math.random() * LOADING_PHRASES.length)];
      setLoadingPhrase(loadingData.text);
      setIsTransitioning(true);

      try {
        const result = await onUpdateUser({ [currentItem.field]: answer });

        // Check if harmful content was detected (only for currentMission field)
        if (currentItem.field === 'currentMission' && result.harmfulContentDetected) {
          console.warn('[OnboardingScreen] ⚠️ Harmful content detected for mission:', answer);
          setIsTransitioning(false);

          // Show error alert
          alert('I can\'t create an action plan for that goal. Please choose something that will help you build your financial confidence and independence.');

          // Stay on the same screen - don't advance
          return;
        }
      } catch (error) {
        console.error('[OnboardingScreen] Failed to save answer:', error);
        // Continue anyway - we'll retry on next session
      }

      // Small delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    // Move to next item or complete
    if (!isLastItem) {
      setCurrentIndex(currentIndex + 1);
      setIsTransitioning(false);
    } else {
      console.log('[OnboardingScreen] Last item reached - completing onboarding');
      onComplete();
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else if (isChangeObjectiveFlow && onDismiss) {
      // If at the first screen of change-objective flow, cancel and go back
      onDismiss();
    }
  };

  // Skip functionality removed - onboarding is now linear and required

  // Show loading state while initializing
  if (!isInitialized || flowItems === null) {
    // console.log('[OnboardingScreen] Still initializing...');
    return null;
  }

  // If truly no items after initialization, this will be handled by the useEffect above
  if (flowItems.length === 0) {
    // console.log('[OnboardingScreen] No flow items after init, returning null (onComplete should fire)');
    return null;
  }

  // Safety check - if index is out of bounds, bail out
  if (currentIndex >= flowItems.length) {
    // console.log('[OnboardingScreen] Index out of bounds, completing...');
    return null;
  }

  const currentItem = flowItems[currentIndex];

  // Safety check
  if (!currentItem) {
    console.error('[OnboardingScreen] currentItem is undefined at index:', currentIndex);
    return null;
  }

  const progress = ((currentIndex + 1) / flowItems.length) * 100;

  // Determine if back button should be shown
  // NEVER show back button during initial onboarding
  // In change-objective flow: Allow back on all screens to enable canceling
  const isDevMode = process.env.NODE_ENV === 'development';
  const isFocusOnOneGoalScreen = currentItem.id === 'focus-one-goal';
  const shouldShowBack = isChangeObjectiveFlow
    && (!isFocusOnOneGoalScreen || isDevMode);

  // console.log('[OnboardingScreen] Rendering, currentItem:', currentItem, 'progress:', progress);

  // Show loading screen during transitions
  if (isTransitioning) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <LoadingScreen
            text={loadingPhrase}
            variant="dots"
            fullScreen={false}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${progress}%` }} />
        </div>

        <div className={styles.fadeIn}>
          {currentItem.type === 'intro' ? (
            // Render special education screens instead of generic IntroScreen
            currentItem.educationType === 'notification' ? (
              <NotificationEducationScreen
                user={user}
                onNext={() => handleNext()}
                onBack={shouldShowBack ? handleBack : null}
              />
            ) : currentItem.educationType === 'pwa' ? (
              <PWAEducationScreen
                onNext={() => handleNext()}
                onBack={shouldShowBack ? handleBack : null}
              />
            ) : (
              <IntroScreen
                title={currentItem.title}
                description={currentItem.description}
                onNext={() => handleNext()}
                onBack={shouldShowBack ? handleBack : null}
                introId={currentItem.id}
              />
            )
          ) : currentItem.type === 'plan-approval' ? (
            // Render plan approval screen
            <PlanApprovalScreen
              user={user}
              onNext={handleNext}
              onBack={shouldShowBack ? handleBack : null}
            />
          ) : currentItem.type === 'todos-loading' ? (
            // Render todos loading screen - waits for approval promise then todos
            <TodosLoadingScreen
              approvalPromise={approvalPromise}
              onNext={() => onComplete()}
            />
          ) : (
            <QuestionScreen
              question={currentItem}
              currentQuestionNumber={flowItems.slice(0, currentIndex + 1).filter(item => item.type === 'question').length}
              totalQuestions={flowItems.filter(item => item.type === 'question').length}
              initialValue={answers[currentItem.field] || ''}
              onNext={handleNext}
              onBack={shouldShowBack ? handleBack : null}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default OnboardingScreen;
